"use client";

import { useSearchParams } from "next/navigation";
import { useActionState, useEffect, useRef, useState } from "react";

import { loginWithEmail, loginWithGoogle } from "@/app/actions/auth.js";
// Import específico del subpath para no arrastrar el barrel completo
// (que incluye módulos con `node:crypto` no bundleables en el cliente).
import { mapAuthError } from "@kaizen/auth-core/map-auth-error";

const PENDING_EMAIL_KEY = "kaizen.pendingEmail";

export default function SignInForm() {
	const [emailState, emailAction, emailPending] = useActionState(
		loginWithEmail,
		undefined,
	);
	const searchParams = useSearchParams();
	const authError = searchParams.get("error");
	const reason = searchParams.get("reason");
	const reasonEmail = searchParams.get("email") ?? "";

	// El email se pierde en el round-trip al callback de Auth.js cuando hay error
	// (Auth.js redirige a `pages.error` sin propagar query/state). Lo persistimos
	// en sessionStorage para que la pantalla de código sobreviva a reintentos.
	const [pendingEmail, setPendingEmail] = useState("");
	useEffect(() => {
		if (emailState?.status === "sent" && emailState.email) {
			sessionStorage.setItem(PENDING_EMAIL_KEY, emailState.email);
			setPendingEmail(emailState.email);
		} else if (reason === "link_expired" && reasonEmail) {
			sessionStorage.setItem(PENDING_EMAIL_KEY, reasonEmail);
			setPendingEmail(reasonEmail);
		} else {
			setPendingEmail(sessionStorage.getItem(PENDING_EMAIL_KEY) ?? "");
		}
	}, [emailState, reason, reasonEmail]);

	const emailForCode =
		emailState?.email ?? pendingEmail ?? "";
	const showCodeScreen =
		emailState?.status === "sent" ||
		(authError !== null && emailForCode !== "");

	const resetPendingEmail = () => {
		sessionStorage.removeItem(PENDING_EMAIL_KEY);
		setPendingEmail("");
	};

	// Reason banner — link de auto-login expirado: pre-rellenamos email y avisamos.
	const reasonMessage =
		reason === "link_expired"
			? "Este enlace ha caducado. Te enviamos un código nuevo si introduces de nuevo tu email."
			: null;

	// Pre-rellenar email cuando llegamos con ?reason=link_expired&email=<...>.
	const initialEmail = reason === "link_expired" ? reasonEmail : "";

	if (showCodeScreen) {
		return (
			<CodeScreen
				email={emailForCode}
				sentMessage={emailState?.message}
				errorMessage={mapAuthError(authError)}
				onBack={resetPendingEmail}
			/>
		);
	}

	const emailErrorMessage =
		emailState?.status === "error"
			? emailState.message
			: authError === "Verification"
				? "El código que introdujiste no es válido o ha caducado. Empieza de nuevo: vuelve a pedir un código."
				: mapAuthError(authError);

	return (
		<EmailScreen
			action={emailAction}
			pending={emailPending}
			initialEmail={initialEmail}
			errorMessage={emailErrorMessage}
			reasonMessage={reasonMessage}
		/>
	);
}

function EmailScreen({
	action,
	pending,
	initialEmail,
	errorMessage,
	reasonMessage,
}) {
	return (
		<>
			<form action={loginWithGoogle}>
				<button
					type="submit"
					className="w-full bg-white border border-border hover:border-olive text-text-primary font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-3"
				>
					<GoogleIcon />
					<span>Continuar con Google</span>
				</button>
			</form>

			<div className="flex items-center gap-3">
				<span className="h-px bg-border flex-1" />
				<span className="text-xs text-text-muted uppercase tracking-widest">
					o
				</span>
				<span className="h-px bg-border flex-1" />
			</div>

			{reasonMessage && (
				<div className="p-3 rounded-lg bg-cream border border-border text-text-secondary text-sm">
					{reasonMessage}
				</div>
			)}

			<form action={action} className="space-y-4">
				<div>
					<label
						htmlFor="email"
						className="block text-sm font-medium text-text-primary mb-2"
					>
						Recibir un código por email
					</label>
					<input
						id="email"
						name="email"
						type="email"
						autoComplete="email"
						required
						defaultValue={initialEmail}
						className="w-full bg-white border border-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-all"
						placeholder="tu@email.com"
					/>
				</div>

				{errorMessage && (
					<div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
						{errorMessage}
					</div>
				)}

				<button
					type="submit"
					disabled={pending}
					className="w-full bg-olive hover:bg-olive-light text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
				>
					{pending ? "Enviando…" : "Enviarme un código"}
				</button>
			</form>
		</>
	);
}

function CodeScreen({ email, sentMessage, errorMessage, onBack }) {
	// Form HTML plano GET directo al callback de Auth.js. Evita server actions
	// porque la redirección desde un server action a una API route de Auth.js no
	// propaga correctamente las cookies de sesión (lección Fase 2 §9.3 informe 09).
	const formRef = useRef(null);
	const [code, setCode] = useState("");
	const [submitting, setSubmitting] = useState(false);

	// Reset cuando llega un error nuevo.
	useEffect(() => {
		if (errorMessage) {
			setCode("");
			setSubmitting(false);
		}
	}, [errorMessage]);

	// Auto-submit al alcanzar 6 dígitos.
	useEffect(() => {
		if (code.length === 6 && /^\d{6}$/.test(code) && !submitting) {
			setSubmitting(true);
			formRef.current?.submit();
		}
	}, [code, submitting]);

	return (
		<form
			ref={formRef}
			method="GET"
			action="/api/auth/callback/email"
			className="space-y-4"
		>
			<div>
				<p className="text-sm text-text-secondary mb-2">
					{sentMessage ??
						"Te hemos enviado un código por email. Revisa tu bandeja."}
				</p>
				{email && (
					<p className="text-xs text-text-muted mb-4">
						Lo enviamos a{" "}
						<span className="text-text-primary">{email}</span>.
						Introdúcelo aquí (también puedes clicar el enlace del
						email si lo abres en este mismo dispositivo).
					</p>
				)}

				<input type="hidden" name="email" value={email} />
				<input type="hidden" name="callbackUrl" value="/dashboard" />

				<label
					htmlFor="token"
					className="block text-sm font-medium text-text-primary mb-2"
				>
					Código de 6 dígitos
				</label>
				<input
					id="token"
					name="token"
					type="text"
					inputMode="numeric"
					pattern="\d{6}"
					maxLength={6}
					autoComplete="one-time-code"
					required
					// biome-ignore lint/a11y/noAutofocus: UX deliberada — la pantalla es modal y el código de 6 dígitos es la única acción
					autoFocus
					value={code}
					onChange={(e) => {
						const cleaned = e.target.value
							.replace(/\D/g, "")
							.slice(0, 6);
						setCode(cleaned);
					}}
					disabled={submitting}
					className="w-full bg-white border border-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-all text-center text-2xl tracking-[0.6em] font-mono disabled:opacity-60"
					placeholder="••••••"
				/>
			</div>

			{errorMessage && (
				<div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
					{errorMessage}
				</div>
			)}

			<button
				type="submit"
				disabled={submitting || code.length !== 6}
				className="w-full bg-olive hover:bg-olive-light text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
			>
				{submitting ? "Entrando…" : "Entrar"}
			</button>

			<p className="text-xs text-text-muted text-center">
				¿Email equivocado?{" "}
				<a
					href="/sign-in"
					onClick={onBack}
					className="text-olive hover:underline"
				>
					Volver
				</a>
			</p>
		</form>
	);
}

function GoogleIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="20"
			height="20"
			viewBox="0 0 48 48"
			aria-hidden="true"
		>
			<title>Google</title>
			<path
				fill="#FFC107"
				d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
			/>
			<path
				fill="#FF3D00"
				d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
			/>
			<path
				fill="#4CAF50"
				d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
			/>
			<path
				fill="#1976D2"
				d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
			/>
		</svg>
	);
}
